/**
 * Service Questionnaire Page
 * 
 * Beautiful AI-powered questionnaire for immigration services:
 * - Real Estate
 * - Employment
 * - English Academy
 * 
 * Features:
 * - Progress visualization sidebar
 * - Conversational AI chat
 * - Auto-focus input
 * - Modern dark theme design
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { IoSend, IoCheckmarkCircle, IoTimeOutline, IoCloseCircle, IoHomeOutline, IoBriefcaseOutline, IoBookOutline, IoMic, IoMicOff, IoStop, IoAttach, IoDocumentText } from 'react-icons/io5';
import { FaSpinner, FaUser, FaRobot, FaFile, FaFilePdf, FaFileWord, FaFileImage } from 'react-icons/fa';
import { HiOutlineSparkles } from 'react-icons/hi';
import { BsChatDots } from 'react-icons/bs';

const API_BASE = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const typing = keyframes`
  0%, 60%, 100% { opacity: 0.4; }
  30% { opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const progressGlow = keyframes`
  0%, 100% { box-shadow: 0 0 5px var(--accent-glow); }
  50% { box-shadow: 0 0 20px var(--accent-glow); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const recording = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
`;

const waveform = keyframes`
  0%, 100% { height: 4px; }
  50% { height: 16px; }
`;

// ============================================
// SERVICE THEME CONFIG
// ============================================

const SERVICE_THEMES = {
  real_estate: {
    icon: '🏠',
    name: 'Real Estate',
    description: 'Find your perfect home in Canada',
    primaryColor: '#3b82f6',
    secondaryColor: '#2563eb',
    accentGlow: 'rgba(59, 130, 246, 0.4)',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    bgGradient: 'linear-gradient(135deg, #0c1929 0%, #1e293b 50%, #0f172a 100%)',
    IconComponent: IoHomeOutline
  },
  employment: {
    icon: '💼',
    name: 'Employment',
    description: 'Build your career profile',
    primaryColor: '#f97316',
    secondaryColor: '#ea580c',
    accentGlow: 'rgba(249, 115, 22, 0.4)',
    gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    bgGradient: 'linear-gradient(135deg, #1a0f07 0%, #2d1810 50%, #1c0e07 100%)',
    IconComponent: IoBriefcaseOutline
  },
  english_academy: {
    icon: '📚',
    name: 'English Academy',
    description: 'Improve your language skills',
    primaryColor: '#8b5cf6',
    secondaryColor: '#7c3aed',
    accentGlow: 'rgba(139, 92, 246, 0.4)',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    bgGradient: 'linear-gradient(135deg, #0f0a1a 0%, #1e1433 50%, #12091f 100%)',
    IconComponent: IoBookOutline
  }
};

// ============================================
// STYLED COMPONENTS
// ============================================

const PageWrapper = styled.div`
  --primary-color: ${props => props.theme?.primaryColor || '#3b82f6'};
  --secondary-color: ${props => props.theme?.secondaryColor || '#2563eb'};
  --accent-glow: ${props => props.theme?.accentGlow || 'rgba(59, 130, 246, 0.4)'};
  
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  /* Keep default gray-blue background - does NOT change with theme */
  background: linear-gradient(135deg, #0c0f1a 0%, #1a1f35 50%, #0f1628 100%);
  display: flex;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Sidebar = styled.div`
  width: 340px;
  height: 100%;
  overflow-y: auto;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.9) 100%);
  border-right: 1px solid rgba(var(--primary-color), 0.15);
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  
  &::-webkit-scrollbar {
    width: 0;
  }
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const ServiceLogo = styled.div`
  width: 72px;
  height: 72px;
  background: ${props => props.theme?.gradient};
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 8px 32px ${props => props.theme?.accentGlow};
  font-size: 36px;
  animation: ${float} 3s ease-in-out infinite;
`;

const ServiceName = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 8px 0;
  background: ${props => props.theme?.gradient || 'linear-gradient(135deg, #fff 0%, #94a3b8 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ServiceSubtitle = styled.p`
  font-size: 14px;
  color: rgba(148, 163, 184, 0.8);
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const ProgressSection = styled.div`
  margin-bottom: 32px;
`;

const ProgressLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(148, 163, 184, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
`;

const CircularProgress = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
  margin: 0 auto 20px;
`;

const CircleSVG = styled.svg`
  transform: rotate(-90deg);
  width: 160px;
  height: 160px;
`;

const CircleBackground = styled.circle`
  fill: none;
  stroke: rgba(148, 163, 184, 0.1);
  stroke-width: 10;
`;

const CircleProgress = styled.circle`
  fill: none;
  stroke: url(#progressGradient);
  stroke-width: 10;
  stroke-linecap: round;
  stroke-dasharray: 440;
  stroke-dashoffset: ${props => 440 - (440 * props.progress) / 100};
  transition: stroke-dashoffset 0.6s ease;
  filter: drop-shadow(0 0 8px ${props => props.theme?.accentGlow});
`;

const ProgressCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const ProgressPercent = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.theme?.primaryColor || '#3b82f6'};
  line-height: 1;
`;

const ProgressText = styled.div`
  font-size: 12px;
  color: rgba(148, 163, 184, 0.7);
  margin-top: 4px;
`;

const DataSection = styled.div`
  margin-top: auto;
`;

const DataLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(148, 163, 184, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const DataItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  animation: ${fadeIn} 0.3s ease-out;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DataKey = styled.span`
  font-size: 12px;
  color: rgba(148, 163, 184, 0.6);
  text-transform: capitalize;
`;

const DataValue = styled.span`
  font-size: 13px;
  color: #e2e8f0;
  font-weight: 500;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
`;

// Chat Container
const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
`;

const ChatHeader = styled.div`
  padding: 20px 32px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.7) 100%);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  display: flex;
  align-items: center;
  gap: 16px;
  backdrop-filter: blur(10px);
`;

const ChatHeaderIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${props => props.theme?.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  box-shadow: 0 4px 16px ${props => props.theme?.accentGlow};
`;

const ChatHeaderInfo = styled.div`
  flex: 1;
`;

const ChatHeaderTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #f1f5f9;
  margin: 0 0 4px 0;
`;

const ChatHeaderSubtitle = styled.p`
  font-size: 13px;
  color: rgba(148, 163, 184, 0.7);
  margin: 0;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.2);
    border-radius: 3px;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  animation: ${fadeIn} 0.4s ease-out;
  ${props => props.isUser && css`
    flex-direction: row-reverse;
  `}
`;

const MessageAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${props => props.isUser 
    ? 'linear-gradient(135deg, #475569 0%, #334155 100%)' 
    : props.theme?.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: ${props => props.isUser 
    ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
    : `0 4px 12px ${props.theme?.accentGlow}`};
`;

const MessageBubble = styled.div`
  max-width: 70%;
  padding: 16px 20px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.6;
  color: #e2e8f0;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  ${props => props.isUser ? css`
    background: linear-gradient(135deg, rgba(71, 85, 105, 0.9) 0%, rgba(51, 65, 85, 0.95) 100%);
    border: 1px solid rgba(100, 116, 139, 0.3);
    border-bottom-right-radius: 6px;
  ` : css`
    background: linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%);
    border: 1px solid ${props.theme?.primaryColor}33;
    border-bottom-left-radius: 6px;
  `}
  
  /* HTML content styling */
  p {
    margin: 0 0 12px 0;
    white-space: pre-wrap;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong, b {
    font-weight: 600;
    color: ${props => props.isUser ? '#fff' : props.theme?.primaryColor || '#60a5fa'};
  }
  
  em, i {
    font-style: italic;
    opacity: 0.9;
  }
  
  ul, ol {
    margin: 12px 0;
    padding-left: 24px;
  }
  
  li {
    margin: 8px 0;
    line-height: 1.5;
  }
  
  a {
    color: ${props => props.theme?.primaryColor || '#60a5fa'};
    text-decoration: underline;
    &:hover {
      opacity: 0.8;
    }
  }
  
  h1, h2, h3, h4 {
    margin: 16px 0 8px 0;
    font-weight: 600;
    color: #f1f5f9;
    &:first-child {
      margin-top: 0;
    }
  }
  
  h1 { font-size: 1.4em; }
  h2 { font-size: 1.25em; }
  h3 { font-size: 1.1em; }
  h4 { font-size: 1em; }
  
  /* Table styling for formatted data */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    font-size: 13px;
  }
  
  th, td {
    padding: 8px 12px;
    text-align: left;
    border: 1px solid rgba(148, 163, 184, 0.2);
  }
  
  th {
    background: rgba(148, 163, 184, 0.1);
    font-weight: 600;
    color: ${props => props.theme?.primaryColor || '#60a5fa'};
  }
  
  tr:nth-child(even) {
    background: rgba(148, 163, 184, 0.05);
  }
  
  /* Code and pre blocks */
  pre, code {
    white-space: pre-wrap;
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 13px;
  }
  
  pre {
    background: rgba(0, 0, 0, 0.3);
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 12px 0;
  }
  
  code {
    background: rgba(148, 163, 184, 0.15);
    padding: 2px 6px;
    border-radius: 4px;
  }
  
  /* Horizontal rules */
  hr {
    border: none;
    border-top: 1px solid rgba(148, 163, 184, 0.2);
    margin: 16px 0;
  }
  
  /* Blockquotes */
  blockquote {
    border-left: 3px solid ${props => props.theme?.primaryColor || '#60a5fa'};
    padding-left: 16px;
    margin: 12px 0;
    opacity: 0.9;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%);
  border: 1px solid ${props => props.theme?.primaryColor}33;
  border-radius: 18px;
  border-bottom-left-radius: 6px;
  max-width: 80px;
`;

const TypingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.theme?.primaryColor};
  animation: ${typing} 1.4s infinite;
  animation-delay: ${props => props.delay}ms;
`;

// Input Area
const InputArea = styled.div`
  padding: 5px 5px 5px;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.7) 0%, rgba(15, 23, 42, 0.95) 100%);
  border-top: 1px solid rgba(148, 163, 184, 0.1);
  backdrop-filter: blur(10px);
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  /* Keep default gray-blue background - does NOT change with theme */
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%);
  border: 1px solid ${props => props.focused ? props.theme?.primaryColor : 'rgba(148, 163, 184, 0.15)'};
  border-radius: 16px;
  padding: 12px 16px;
  transition: all 0.2s ease;
  
  ${props => props.focused && css`
    box-shadow: 0 0 0 3px ${props.theme?.accentGlow};
  `}
`;

const TextInput = styled.textarea`
  flex: 1;
  background: transparent;
  border: none;
  color: #e2e8f0;
  font-size: 15px;
  font-family: inherit;
  resize: none;
  min-height: 24px;
  max-height: 120px;
  line-height: 1.5;
  
  &::placeholder {
    color: rgba(148, 163, 184, 0.5);
  }
  
  &:focus {
    outline: none;
  }
`;

const SendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => props.disabled 
    ? 'rgba(71, 85, 105, 0.5)' 
    : props.theme?.gradient};
  border: none;
  color: white;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  ${props => !props.disabled && css`
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 16px ${props.theme?.accentGlow};
    }
  `}
`;

// Voice & File Upload Buttons
const ActionButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.isRecording 
    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    : 'rgba(71, 85, 105, 0.3)'};
  border: 1px solid ${props => props.isRecording 
    ? 'rgba(239, 68, 68, 0.5)'
    : 'rgba(148, 163, 184, 0.2)'};
  color: ${props => props.isRecording ? '#fff' : 'rgba(148, 163, 184, 0.8)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  ${props => props.isRecording && css`
    animation: ${recording} 1.5s ease-in-out infinite;
  `}
  
  &:hover {
    background: ${props => props.isRecording 
      ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
      : 'rgba(71, 85, 105, 0.5)'};
    color: ${props => props.isRecording ? '#fff' : '#e2e8f0'};
    border-color: ${props => props.isRecording 
      ? 'rgba(239, 68, 68, 0.7)'
      : 'rgba(148, 163, 184, 0.4)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RecordingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.2) 100%);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 20px;
  font-size: 12px;
  color: #ef4444;
  font-weight: 500;
`;

const RecordingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  animation: ${pulse} 1s ease-in-out infinite;
`;

const RecordingTime = styled.span`
  font-variant-numeric: tabular-nums;
`;

const TranscribingStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.2) 100%);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 20px;
  font-size: 12px;
  color: #3b82f6;
  font-weight: 500;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FilePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const FilePreviewItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(71, 85, 105, 0.3);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  font-size: 12px;
  color: #e2e8f0;
`;

const FileIcon = styled.span`
  font-size: 16px;
  color: ${props => props.theme?.primaryColor || '#3b82f6'};
`;

const FileName = styled.span`
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: rgba(148, 163, 184, 0.6);
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
  }
`;

const UploadedDocumentsSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
`;

const UploadedDocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.05);
  animation: ${fadeIn} 0.3s ease-out;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DocIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.theme?.gradient || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
`;

const DocInfo = styled.div`
  flex: 1;
`;

const DocName = styled.div`
  font-size: 12px;
  color: #e2e8f0;
  font-weight: 500;
`;

const DocMeta = styled.div`
  font-size: 10px;
  color: rgba(148, 163, 184, 0.6);
`;

const InputActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Complete Screen
const CompleteScreen = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  animation: ${fadeIn} 0.5s ease-out;
`;

const CompleteIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${props => props.theme?.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  margin-bottom: 24px;
  box-shadow: 0 8px 40px ${props => props.theme?.accentGlow};
  animation: ${pulse} 2s ease-in-out infinite;
`;

const CompleteTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 12px 0;
`;

const CompleteSubtitle = styled.p`
  font-size: 16px;
  color: rgba(148, 163, 184, 0.8);
  margin: 0 0 32px 0;
  max-width: 400px;
  line-height: 1.6;
`;

const CompleteButton = styled.button`
  background: ${props => props.theme?.gradient};
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px ${props => props.theme?.accentGlow};
  }
`;

// Loading State
const LoadingContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0c0f1a 0%, #1a1f35 50%, #0f1628 100%);
  gap: 20px;
`;

const LoadingSpinner = styled(FaSpinner)`
  font-size: 40px;
  color: #6366f1;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: rgba(148, 163, 184, 0.7);
`;

// Error State
const ErrorContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0c0f1a 0%, #1a1f35 50%, #0f1628 100%);
  text-align: center;
  padding: 40px;
`;

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: #ef4444;
  margin-bottom: 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 12px 0;
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: rgba(148, 163, 184, 0.7);
  margin: 0;
`;

// ============================================
// MAIN COMPONENT
// ============================================

const ServiceQuestionnairePage = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [collectedData, setCollectedData] = useState({});
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionStatus, setTranscriptionStatus] = useState('');
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  
  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const recordingTimerRef = useRef(null);
  
  const theme = session?.serviceType ? SERVICE_THEMES[session.serviceType] : SERVICE_THEMES.real_estate;

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input after response
  useEffect(() => {
    if (!isTyping && !isSending && !isRecording && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isTyping, isSending, isRecording]);

  // Recording timer
  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(recordingTimerRef.current);
      setRecordingTime(0);
    }
    return () => clearInterval(recordingTimerRef.current);
  }, [isRecording]);

  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle voice recording
  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }
      setIsRecording(false);
    } else {
      // Start recording
      try {
        setAudioChunks([]);
        setTranscriptionStatus('');
        
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: { 
            echoCancellation: true, 
            noiseSuppression: true 
          } 
        });
        
        const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
          ? 'audio/webm' 
          : 'audio/mp4';
        
        const recorder = new MediaRecorder(stream, { mimeType });
        const chunks = [];
        
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };
        
        recorder.onstop = async () => {
          stream.getTracks().forEach(track => track.stop());
          
          if (chunks.length > 0) {
            const audioBlob = new Blob(chunks, { type: mimeType });
            if (audioBlob.size > 1000) {
              await transcribeAudio(audioBlob);
            } else {
              setTranscriptionStatus('Recording too short');
              setTimeout(() => setTranscriptionStatus(''), 3000);
            }
          }
        };
        
        recorder.onerror = () => {
          setTranscriptionStatus('Recording error');
          setTimeout(() => setTranscriptionStatus(''), 3000);
          stream.getTracks().forEach(track => track.stop());
          setIsRecording(false);
        };
        
        setMediaRecorder(recorder);
        recorder.start(1000);
        setIsRecording(true);
      } catch (err) {
        console.error('Error starting recording:', err);
        setTranscriptionStatus('Microphone access denied');
        setTimeout(() => setTranscriptionStatus(''), 3000);
      }
    }
  };

  // Transcribe audio with Whisper
  const transcribeAudio = async (audioBlob) => {
    try {
      setIsTranscribing(true);
      setTranscriptionStatus('Transcribing...');
      
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const response = await fetch(`${API_BASE}/api/transcribe`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Transcription failed');
      }
      
      const data = await response.json();
      
      if (data.transcription && data.transcription.trim()) {
        setInputValue(prev => {
          const prefix = prev.trim() ? `${prev.trim()} ` : '';
          return `${prefix}${data.transcription.trim()}`;
        });
        setTranscriptionStatus('');
        
        // Focus input after transcription
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
      } else {
        setTranscriptionStatus('No speech detected');
        setTimeout(() => setTranscriptionStatus(''), 3000);
      }
    } catch (err) {
      console.error('Transcription error:', err);
      setTranscriptionStatus('Transcription failed');
      setTimeout(() => setTranscriptionStatus(''), 3000);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Remove selected file
  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload files and process
  const uploadAndProcessFiles = async () => {
    if (selectedFiles.length === 0) return null;
    
    setIsUploadingFile(true);
    
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      // Upload files
      const uploadResponse = await axios.post(
        `${API_BASE}/service-questionnaire/sessions/${sessionId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (uploadResponse.data.success) {
        const fileNames = selectedFiles.map(f => f.name);
        
        // Add to uploaded documents list
        const newDocs = selectedFiles.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }));
        setUploadedDocuments(prev => [...prev, ...newDocs]);
        
        // Clear selected files
        setSelectedFiles([]);
        
        return {
          fileNames,
          extractedData: uploadResponse.data.extractedData || {}
        };
      }
      return null;
    } catch (err) {
      console.error('File upload error:', err);
      return null;
    } finally {
      setIsUploadingFile(false);
    }
  };

  // Get file icon
  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return <FaFilePdf />;
      case 'doc': case 'docx': return <FaFileWord />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': return <FaFileImage />;
      default: return <FaFile />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Load session
  useEffect(() => {
    const loadSession = async () => {
      try {
        setIsLoading(true);
        
        // Get session info
        const sessionRes = await axios.get(`${API_BASE}/service-questionnaire/sessions/${sessionId}`);
        
        if (!sessionRes.data.success) {
          setError(sessionRes.data.error || 'Session not found');
          return;
        }

        setSession(sessionRes.data.session);

        // If session is already completed
        if (sessionRes.data.session.status === 'completed') {
          setIsComplete(true);
          return;
        }

        // Start session and get initial message
        const startRes = await axios.get(`${API_BASE}/service-questionnaire/sessions/${sessionId}/start`);
        
        if (startRes.data.success) {
          setSession(startRes.data.session);
          setMessages([{
            role: 'assistant',
            content: startRes.data.message
          }]);
          
          // Set existing user data for display
          if (startRes.data.session.existingUserData) {
            setCollectedData(startRes.data.session.existingUserData);
          }
        }
      } catch (err) {
        console.error('Error loading session:', err);
        setError(err.response?.data?.error || 'Failed to load questionnaire');
      } finally {
        setIsLoading(false);
      }
    };

    if (sessionId) {
      loadSession();
    }
  }, [sessionId]);

  // Send message
  const handleSendMessage = async () => {
    if ((!inputValue.trim() && selectedFiles.length === 0) || isSending) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsSending(true);
    
    // Handle file upload first if files are selected
    let fileInfo = null;
    if (selectedFiles.length > 0) {
      fileInfo = await uploadAndProcessFiles();
      
      // Add file upload message
      const fileMessage = fileInfo 
        ? `📎 Uploaded: ${fileInfo.fileNames.join(', ')}`
        : '📎 Files uploaded';
      
      setMessages(prev => [...prev, { 
        role: 'user', 
        content: userMessage ? `${fileMessage}\n\n${userMessage}` : fileMessage,
        hasFiles: true,
        fileNames: fileInfo?.fileNames
      }]);
    } else if (userMessage) {
      // Add user message immediately
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    }
    
    // Show typing indicator
    setIsTyping(true);

    try {
      // Build message with file context if applicable
      let messageToSend = userMessage;
      if (fileInfo?.extractedData && Object.keys(fileInfo.extractedData).length > 0) {
        messageToSend = `${userMessage}\n\n[Extracted from uploaded files: ${JSON.stringify(fileInfo.extractedData)}]`;
        
        // Update collected data with extracted info
        setCollectedData(prev => ({ ...prev, ...fileInfo.extractedData }));
      }

      const response = await axios.post(
        `${API_BASE}/service-questionnaire/sessions/${sessionId}/message`,
        { 
          message: messageToSend || 'I have uploaded documents for you to review.',
          hasFiles: !!fileInfo,
          fileNames: fileInfo?.fileNames
        }
      );

      if (response.data.success) {
        // Add AI response
        setMessages(prev => [...prev, { role: 'assistant', content: response.data.message }]);
        
        // Update collected data
        if (response.data.extractedData && Object.keys(response.data.extractedData).length > 0) {
          setCollectedData(prev => ({ ...prev, ...response.data.extractedData }));
        }

        // Update session
        if (response.data.session) {
          setSession(prev => ({
            ...prev,
            ...response.data.session
          }));
        }

        // Check if complete
        if (response.data.isComplete) {
          setTimeout(async () => {
            // Complete the session
            await axios.post(`${API_BASE}/service-questionnaire/sessions/${sessionId}/complete`);
            setIsComplete(true);
          }, 2000);
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, there was an error processing your message. Please try again.' 
      }]);
    } finally {
      setIsSending(false);
      setIsTyping(false);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Complete questionnaire manually
  const handleComplete = async () => {
    try {
      await axios.post(`${API_BASE}/service-questionnaire/sessions/${sessionId}/complete`);
      setIsComplete(true);
    } catch (err) {
      console.error('Error completing session:', err);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Loading your questionnaire...</LoadingText>
      </LoadingContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <ErrorContainer>
        <ErrorIcon>
          <IoCloseCircle />
        </ErrorIcon>
        <ErrorTitle>Unable to Load Questionnaire</ErrorTitle>
        <ErrorText>{error}</ErrorText>
      </ErrorContainer>
    );
  }

  // Complete state
  if (isComplete) {
    return (
      <PageWrapper theme={theme}>
        <Sidebar>
          <ServiceLogo theme={theme}>{theme.icon}</ServiceLogo>
          <ServiceName theme={theme}>{theme.name}</ServiceName>
          <ServiceSubtitle>{theme.description}</ServiceSubtitle>
          
          <ProgressSection>
            <ProgressLabel>Completion</ProgressLabel>
            <CircularProgress>
              <CircleSVG>
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={theme.primaryColor} />
                    <stop offset="100%" stopColor={theme.secondaryColor} />
                  </linearGradient>
                </defs>
                <CircleBackground cx="80" cy="80" r="70" />
                <CircleProgress cx="80" cy="80" r="70" progress={100} theme={theme} />
              </CircleSVG>
              <ProgressCenter>
                <ProgressPercent theme={theme}>100%</ProgressPercent>
                <ProgressText>Complete</ProgressText>
              </ProgressCenter>
            </CircularProgress>
          </ProgressSection>
        </Sidebar>

        <CompleteScreen>
          <CompleteIcon theme={theme}>
            <IoCheckmarkCircle />
          </CompleteIcon>
          <CompleteTitle>Thank You!</CompleteTitle>
          <CompleteSubtitle>
            Your {theme.name.toLowerCase()} profile has been completed successfully. 
            Our team will review your information and be in touch shortly.
          </CompleteSubtitle>
          <CompleteButton theme={theme} onClick={() => window.close()}>
            Close Window
          </CompleteButton>
        </CompleteScreen>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper theme={theme}>
      {/* Sidebar */}
      <Sidebar>
        <ServiceLogo theme={theme}>{theme.icon}</ServiceLogo>
        <ServiceName theme={theme}>{theme.name}</ServiceName>
        <ServiceSubtitle>{theme.description}</ServiceSubtitle>
        
        <ProgressSection>
          <ProgressLabel>Progress</ProgressLabel>
          <CircularProgress>
            <CircleSVG>
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={theme.primaryColor} />
                  <stop offset="100%" stopColor={theme.secondaryColor} />
                </linearGradient>
              </defs>
              <CircleBackground cx="80" cy="80" r="70" />
              <CircleProgress 
                cx="80" 
                cy="80" 
                r="70" 
                progress={session?.completionPercent || 0} 
                theme={theme}
              />
            </CircleSVG>
            <ProgressCenter>
              <ProgressPercent theme={theme}>{session?.completionPercent || 0}%</ProgressPercent>
              <ProgressText>Complete</ProgressText>
            </ProgressCenter>
          </CircularProgress>
        </ProgressSection>

        {Object.keys(collectedData).length > 0 && (
          <DataSection>
            <DataLabel>Your Profile</DataLabel>
            {Object.entries(collectedData).slice(0, 8).map(([key, value]) => (
              <DataItem key={key}>
                <DataKey>{key.replace(/([A-Z])/g, ' $1').trim()}</DataKey>
                <DataValue>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</DataValue>
              </DataItem>
            ))}
          </DataSection>
        )}
        
        {uploadedDocuments.length > 0 && (
          <UploadedDocumentsSection>
            <DataLabel>Uploaded Documents</DataLabel>
            {uploadedDocuments.map((doc, index) => (
              <UploadedDocItem key={index}>
                <DocIcon theme={theme}>
                  {getFileIcon(doc.name)}
                </DocIcon>
                <DocInfo>
                  <DocName>{doc.name}</DocName>
                  <DocMeta>{formatFileSize(doc.size)} • {doc.uploadedAt}</DocMeta>
                </DocInfo>
              </UploadedDocItem>
            ))}
          </UploadedDocumentsSection>
        )}
      </Sidebar>

      {/* Chat Area */}
      <ChatContainer>
        <ChatHeader>
          <ChatHeaderIcon theme={theme}>
            <HiOutlineSparkles />
          </ChatHeaderIcon>
          <ChatHeaderInfo>
            <ChatHeaderTitle>AI Assistant</ChatHeaderTitle>
            <ChatHeaderSubtitle>I'll help you complete your {theme.name.toLowerCase()} profile</ChatHeaderSubtitle>
          </ChatHeaderInfo>
        </ChatHeader>

        <MessagesContainer>
          {messages.map((msg, index) => (
            <MessageWrapper key={index} isUser={msg.role === 'user'}>
              <MessageAvatar isUser={msg.role === 'user'} theme={theme}>
                {msg.role === 'user' ? <FaUser /> : <FaRobot />}
              </MessageAvatar>
              <MessageBubble 
                isUser={msg.role === 'user'} 
                theme={theme}
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(
                    msg.content, 
                    { 
                      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'b', 'i', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'pre', 'code', 'hr', 'blockquote'],
                      ALLOWED_ATTR: ['href', 'target', 'style', 'class', 'colspan', 'rowspan']
                    }
                  ) 
                }}
              />
            </MessageWrapper>
          ))}
          
          {isTyping && (
            <MessageWrapper isUser={false}>
              <MessageAvatar isUser={false} theme={theme}>
                <FaRobot />
              </MessageAvatar>
              <TypingIndicator theme={theme}>
                <TypingDot theme={theme} delay={0} />
                <TypingDot theme={theme} delay={200} />
                <TypingDot theme={theme} delay={400} />
              </TypingIndicator>
            </MessageWrapper>
          )}
          
          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputArea>
          {/* File Preview */}
          {selectedFiles.length > 0 && (
            <FilePreviewContainer>
              {selectedFiles.map((file, index) => (
                <FilePreviewItem key={index}>
                  <FileIcon theme={theme}>{getFileIcon(file.name)}</FileIcon>
                  <FileName>{file.name}</FileName>
                  <RemoveFileButton onClick={() => removeSelectedFile(index)}>
                    <IoCloseCircle size={16} />
                  </RemoveFileButton>
                </FilePreviewItem>
              ))}
            </FilePreviewContainer>
          )}

          {/* Recording / Transcribing Status */}
          {isRecording && (
            <RecordingIndicator>
              <RecordingDot />
              <span>Recording</span>
              <RecordingTime>{formatTime(recordingTime)}</RecordingTime>
            </RecordingIndicator>
          )}
          
          {isTranscribing && (
            <TranscribingStatus>
              <FaSpinner className="spin" style={{ animation: 'spin 1s linear infinite' }} />
              <span>{transcriptionStatus || 'Transcribing...'}</span>
            </TranscribingStatus>
          )}
          
          {!isRecording && !isTranscribing && transcriptionStatus && (
            <TranscribingStatus>
              <span>{transcriptionStatus}</span>
            </TranscribingStatus>
          )}

          <InputWrapper focused={inputFocused} theme={theme}>
            <InputActions>
              {/* File Upload Button */}
              <ActionButton
                onClick={() => fileInputRef.current?.click()}
                disabled={isSending || isUploadingFile}
                title="Upload files (resume, documents)"
              >
                {isUploadingFile ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <IoAttach />}
              </ActionButton>
              
              {/* Voice Recording Button */}
              <ActionButton
                isRecording={isRecording}
                onClick={toggleRecording}
                disabled={isSending || isTranscribing}
                title={isRecording ? 'Stop recording' : 'Start voice input'}
              >
                {isRecording ? <IoStop /> : <IoMic />}
              </ActionButton>
            </InputActions>
            
            <TextInput
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder={isRecording ? "Recording... speak now" : "Type your response or use voice input..."}
              rows={1}
              disabled={isSending || isRecording}
            />
            
            <SendButton 
              theme={theme}
              disabled={(!inputValue.trim() && selectedFiles.length === 0) || isSending || isRecording}
              onClick={handleSendMessage}
            >
              {isSending ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <IoSend />}
            </SendButton>
          </InputWrapper>
          
          {/* Hidden File Input */}
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.csv"
            onChange={handleFileSelect}
          />
        </InputArea>
      </ChatContainer>
    </PageWrapper>
  );
};

export default ServiceQuestionnairePage;
